/**
 * Orchestrates one Task Intelligence pass: gather project context, call the
 * provider (edge function by default, injectable for tests/eval), validate
 * and shape the response for the review screen. Nothing here writes to the
 * database — see taskDraftModel.js for why that's deliberate.
 */
import { assertCan, CAPABILITY } from "../helpers/permissions";
import { gatherTaskIntelligenceContext, todayIsoDate } from "./gatherTaskIntelligenceContext";
import { createDefaultTaskIntelligenceProvider } from "./taskIntelligenceClient";
import { groupDrafts, splitDrafts } from "./taskDraftModel";

export class EmptyBriefError extends Error {
  constructor() {
    super("Enter or record a brief before analyzing.");
    this.code = "empty_brief";
  }
}

/**
 * @param {object} actor
 * @param {{ projectId: string, projectName?: string, briefText: string,
 *   source?: "voice"|"text", locations?: object[], directReportIds?: string[],
 *   provider?: { analyze: Function } }} options
 */
export async function analyzeBrief(
  actor,
  { projectId, projectName = "", briefText, source = "text", locations = [], directReportIds, provider } = {},
) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_VIEW);
  const text = String(briefText || "").trim();
  if (!text) throw new EmptyBriefError();

  const context = await gatherTaskIntelligenceContext(actor, { projectId, locations, directReportIds });
  const referenceDate = todayIsoDate();
  const activeProvider = provider || createDefaultTaskIntelligenceProvider();

  const result = await activeProvider.analyze({
    projectId,
    briefText: text,
    source,
    referenceDate,
    projectName,
    existingTasks: context.existingTasks,
    members: context.members,
    locations: context.locations,
  });

  const { drafts, noAction } = splitDrafts(result.drafts || []);
  const existingTasksById = new Map(context.existingTasks.map((t) => [t.id, t]));

  return {
    groups: groupDrafts(drafts),
    drafts,
    noAction,
    notes: result.notes || [],
    existingTasksById,
    context,
    referenceDate,
  };
}
